import React, { useState, useEffect, useRef } from 'react'
import { Modal } from './ui'
import { API_BASE_URL } from '../data/meta'
import { CheckCircleIcon, XCircleIcon, PersonIcon } from '../icons'

type Props = {
    isOpen: boolean
    onClose: () => void
    onVolunteerCreated: (volunteer: CreatedVolunteer) => void
}

export type CreatedVolunteer = {
    csid: number
    first_name: string
    last_name: string
    email: string
    phone: string
    country: string
    jamat_khana: string
    gender: string
    profile_photo?: string
}

export const CreateVolunteerModal = (props: Props) => {
    const { isOpen, onClose, onVolunteerCreated } = props

    const [firstName, setFirstName] = useState('')
    const [lastName, setLastName] = useState('')
    const [email, setEmail] = useState('')
    const [phone, setPhone] = useState('')
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState<string | null>(null)
    const [successMessage, setSuccessMessage] = useState<string | null>(null)

    const firstNameRef = useRef<HTMLInputElement>(null)

    // Reset form when modal opens
    useEffect(() => {
        if (isOpen) {
            setFirstName('')
            setLastName('')
            setEmail('')
            setPhone('')
            setError(null)
            setSuccessMessage(null)
            // Focus the first name input
            setTimeout(() => firstNameRef.current?.focus(), 100)
        }
    }, [isOpen])

    // Clear success message after delay
    useEffect(() => {
        if (successMessage) {
            const timer = setTimeout(() => setSuccessMessage(null), 3000)
            return () => clearTimeout(timer)
        }
    }, [successMessage])

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault()

        if (!firstName.trim() || !lastName.trim()) {
            setError('First name and last name are required')
            return
        }

        setLoading(true)
        setError(null)

        try {
            const response = await fetch(`${API_BASE_URL}/check-in/volunteer`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                credentials: 'include',
                body: JSON.stringify({
                    first_name: firstName.trim(),
                    last_name: lastName.trim(),
                    email: email.trim() || null,
                    phone: phone.trim() || null
                })
            })

            if (!response.ok) {
                const data = await response.json()
                throw new Error(data.error || 'Failed to create volunteer')
            }

            const data = await response.json()
            setSuccessMessage('Volunteer created successfully!')

            // Notify parent and close modal after a brief delay
            setTimeout(() => {
                onVolunteerCreated(data.volunteer)
                onClose()
            }, 500)

        } catch (err) {
            setError(err instanceof Error ? err.message : 'An error occurred')
        } finally {
            setLoading(false)
        }
    }

    return (
        <Modal isOpen={isOpen} onClose={onClose} title="Create New Volunteer" maxWidth="md">
            <form onSubmit={handleSubmit} className="p-6">
                <div className="flex items-center gap-3 mb-4 pb-4 border-b border-gray-200 dark:border-gray-700">
                    <div className="w-12 h-12 rounded-full bg-primary-100 dark:bg-primary-900/30 flex items-center justify-center">
                        <PersonIcon className="w-6 h-6 text-primary-600 dark:text-primary-400" />
                    </div>
                    <p className="text-gray-600 dark:text-gray-400">
                        Can't find the volunteer? Create a new record with their basic information.
                    </p>
                </div>

                {/* Success Message */}
                {successMessage && (
                    <div className="mb-4 p-3 flex items-center gap-2 bg-success-50 dark:bg-success-900/20 border border-success-300 dark:border-success-700 rounded-lg text-success-700 dark:text-success-400">
                        <CheckCircleIcon className="w-5 h-5 flex-shrink-0" />
                        <span>{successMessage}</span>
                    </div>
                )}

                {/* Error Message */}
                {error && (
                    <div className="mb-4 p-3 flex items-center gap-2 bg-danger-50 dark:bg-danger-900/20 border border-danger-300 dark:border-danger-700 rounded-lg text-danger-700 dark:text-danger-400">
                        <XCircleIcon className="w-5 h-5 flex-shrink-0" />
                        <span>{error}</span>
                    </div>
                )}

                <div className="space-y-4">
                    {/* First Name */}
                    <div>
                        <label htmlFor="firstName" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                            First Name <span className="text-danger-500">*</span>
                        </label>
                        <input
                            ref={firstNameRef}
                            id="firstName"
                            type="text"
                            value={firstName}
                            onChange={(e) => setFirstName(e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                            placeholder="Enter first name"
                            required
                        />
                    </div>

                    {/* Last Name */}
                    <div>
                        <label htmlFor="lastName" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                            Last Name <span className="text-danger-500">*</span>
                        </label>
                        <input
                            id="lastName"
                            type="text"
                            value={lastName}
                            onChange={(e) => setLastName(e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                            placeholder="Enter last name"
                            required
                        />
                    </div>

                    {/* Email (optional) */}
                    <div>
                        <label htmlFor="email" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                            Email <span className="text-gray-400">(optional)</span>
                        </label>
                        <input
                            id="email"
                            type="email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                            placeholder="Enter email address"
                        />
                    </div>

                    {/* Phone (optional) */}
                    <div>
                        <label htmlFor="phone" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                            Phone <span className="text-gray-400">(optional)</span>
                        </label>
                        <input
                            id="phone"
                            type="tel"
                            value={phone}
                            onChange={(e) => setPhone(e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                            placeholder="Enter phone number"
                        />
                    </div>
                </div>

                {/* Footer */}
                <div className="mt-6 flex flex-col-reverse sm:flex-row justify-end gap-3">
                    <button
                        type="button"
                        onClick={onClose}
                        className="w-full sm:w-auto px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-gray-500"
                    >
                        Cancel
                    </button>
                    <button
                        type="submit"
                        disabled={loading || !firstName.trim() || !lastName.trim()}
                        className="w-full sm:w-auto px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        {loading ? 'Creating...' : 'Create & Allocate'}
                    </button>
                </div>
            </form>
        </Modal>
    )
}
